class RouxStory extends HTMLElement {
  constructor() {
    super();
    this.index = 0;
    this.isOpen = false;
    this.isPaused = false;
    this.startedAt = 0;
    this.pausedAt = 0;
    this.duration = 5000;
    this.raf = null;
    this.previousFocus = null;
    this.boundTick = this.tick.bind(this);
    this.boundKeydown = this.onKeydown.bind(this);
  }

  connectedCallback() {
    this.overlay = this.querySelector('.roux-story__overlay');
    this.frame = this.querySelector('.roux-story__frame');
    this.slides = [...this.querySelectorAll('[data-roux-story-slide]')];
    this.bars = [...this.querySelectorAll('.roux-story__bar')];
    this.pauseButton = this.querySelector('[data-roux-story-pause]');
    this.loop = this.dataset.loop === 'true';
    this.fallbackDuration = Number(this.dataset.duration || 5000);

    if (!this.overlay || this.slides.length === 0) {
      this.setTriggerState(false);
      return;
    }

    this.hydrateExternalTriggers();
    this.bindEvents();
  }

  bindEvents() {
    document.querySelectorAll('[data-roux-story-open]').forEach((trigger) => {
      trigger.addEventListener('click', () => this.open(trigger));
    });

    this.querySelectorAll('[data-roux-story-close]').forEach((button) => {
      button.addEventListener('click', () => this.close());
    });

    this.querySelector('[data-roux-story-prev]')?.addEventListener('click', () => this.prev());
    this.querySelector('[data-roux-story-next]')?.addEventListener('click', () => this.next());
    this.pauseButton?.addEventListener('click', () => this.togglePause());
  }

  hydrateExternalTriggers() {
    const iconUrl = this.dataset.icon;

    document.querySelectorAll('.roux-story-trigger').forEach((trigger) => {
      trigger.hidden = false;
      if (!iconUrl) return;

      const image = trigger.querySelector('.roux-story-trigger__image');
      if (image) {
        image.src = iconUrl;
        image.alt = '';
      }
    });
  }

  setTriggerState(enabled) {
    document.querySelectorAll('.roux-story-trigger').forEach((trigger) => {
      trigger.hidden = !enabled;
    });
  }

  open(trigger) {
    if (this.isOpen) return;
    this.previousFocus = trigger || document.activeElement;
    this.isOpen = true;
    this.isPaused = false;
    this.index = 0;
    this.overlay.hidden = false;
    document.body.classList.add('roux-story-open', 'overflow-hidden');
    document.addEventListener('keydown', this.boundKeydown);
    this.pauseButton?.classList.remove('is-paused');
    this.pauseButton?.setAttribute('aria-label', 'Pause story');
    this.render();
    if (typeof trapFocus === 'function') {
      trapFocus(this.overlay, this.frame);
    } else {
      this.frame?.focus();
    }
  }

  close() {
    if (!this.isOpen) return;
    this.isOpen = false;
    this.overlay.hidden = true;
    document.body.classList.remove('roux-story-open', 'overflow-hidden');
    document.removeEventListener('keydown', this.boundKeydown);
    this.stopAnimation();
    this.stopAllVideos();
    if (typeof removeTrapFocus === 'function') {
      removeTrapFocus(this.previousFocus);
    } else {
      this.previousFocus?.focus?.();
    }
  }

  render() {
    this.stopAnimation();
    this.stopAllVideos();

    this.slides.forEach((slide, slideIndex) => {
      slide.classList.toggle('is-active', slideIndex === this.index);
    });

    this.bars.forEach((bar, barIndex) => {
      bar.classList.toggle('is-complete', barIndex < this.index);
      const fill = bar.querySelector('.roux-story__bar-fill');
      if (fill) fill.style.transform = barIndex < this.index ? 'scaleX(1)' : 'scaleX(0)';
    });

    this.activeSlide = this.slides[this.index];
    this.activeVideo = this.activeSlide?.querySelector('video') || null;
    this.activeFill = this.bars[this.index]?.querySelector('.roux-story__bar-fill') || null;

    if (this.activeVideo) {
      this.prepareVideo();
    } else {
      this.startProgress(this.fallbackDuration);
    }
  }

  prepareVideo() {
    const startVideo = () => {
      this.duration = Number.isFinite(this.activeVideo.duration) && this.activeVideo.duration > 0
        ? this.activeVideo.duration * 1000
        : this.fallbackDuration;
      this.activeVideo.currentTime = 0;
      this.activeVideo.muted = true;
      this.activeVideo.playsInline = true;

      const playPromise = this.activeVideo.play();
      if (playPromise?.catch) playPromise.catch(() => {});

      this.startProgress(this.duration);
    };

    if (this.activeVideo.readyState >= 1) {
      startVideo();
    } else {
      this.activeVideo.addEventListener('loadedmetadata', startVideo, { once: true });
      this.startProgress(this.fallbackDuration);
    }
  }

  startProgress(duration) {
    this.duration = Math.max(500, duration || this.fallbackDuration);
    this.startedAt = performance.now();
    this.pausedAt = 0;
    this.raf = requestAnimationFrame(this.boundTick);
  }

  tick(now) {
    if (!this.isOpen || this.isPaused) return;

    const elapsed = now - this.startedAt;
    const progress = Math.min(1, elapsed / this.duration);

    if (this.activeFill) {
      this.activeFill.style.transform = `scaleX(${progress})`;
    }

    if (progress >= 1) {
      this.next();
      return;
    }

    this.raf = requestAnimationFrame(this.boundTick);
  }

  stopAnimation() {
    if (this.raf) cancelAnimationFrame(this.raf);
    this.raf = null;
  }

  stopAllVideos() {
    this.querySelectorAll('video').forEach((video) => {
      video.pause();
      video.currentTime = 0;
    });
  }

  next() {
    if (this.index >= this.slides.length - 1) {
      if (this.loop) {
        this.index = 0;
        this.render();
      } else {
        this.close();
      }
      return;
    }

    this.index += 1;
    this.render();
  }

  prev() {
    if (this.index === 0) {
      this.render();
      return;
    }

    this.index -= 1;
    this.render();
  }

  togglePause() {
    if (!this.isOpen) return;
    this.isPaused = !this.isPaused;
    this.pauseButton?.classList.toggle('is-paused', this.isPaused);
    this.pauseButton?.setAttribute('aria-label', this.isPaused ? 'Play story' : 'Pause story');

    if (this.isPaused) {
      this.pausedAt = performance.now();
      this.activeVideo?.pause();
      this.stopAnimation();
    } else {
      if (this.pausedAt) {
        this.startedAt += performance.now() - this.pausedAt;
      }
      this.activeVideo?.play?.().catch?.(() => {});
      this.raf = requestAnimationFrame(this.boundTick);
    }
  }

  onKeydown(event) {
    if (!this.isOpen) return;

    if (event.key === 'Escape') this.close();
    if (event.key === 'ArrowRight') this.next();
    if (event.key === 'ArrowLeft') this.prev();
    if (event.key === ' ') {
      event.preventDefault();
      this.togglePause();
    }
  }
}

if (!customElements.get('roux-story')) {
  customElements.define('roux-story', RouxStory);
}
